import { asyncCommandState, isAsyncCommand } from "./AsyncCommandResult.utils";
import { t } from "../../i18n";
import { formatToolDuration } from "./ToolEventTimestamp.utils";
import {
  CheckCircle2,
  ChevronDown,
  LoaderCircle,
  ShieldAlert,
} from "lucide-react";
import { ArtifactResult, ToolResultView } from "../tools/ToolResultView";
import { AnsweredQuestion } from "../AnsweredQuestion";
import { QuestionPrompt } from "../QuestionPrompt";
import { TodoUpdateActivity } from "./TodoUpdateActivity";
import { WorkspaceApprovalActions } from "../agent-workspaces/WorkspaceApprovalActions";
import {
  useInteractionDisclosure,
  toolPresentation,
} from "./MessageActivity.utils";
import { asRecord, stringValue } from "./ToolResultView.utils";
import type { ApprovalHandler, QuestionAnswerHandler } from "./MessageActivity";
import type { AgentWorkspaceApproval, ToolEvent } from "@lumen/shared";

export function ToolActivityCard({
  event,
  approval,
  busy,
  onApproval,
  onContinue,
  onQuestionAnswer,
}: {
  event: ToolEvent;
  approval?: AgentWorkspaceApproval;
  busy: boolean;
  onApproval?: ApprovalHandler;
  onContinue?: () => void;
  onQuestionAnswer?: QuestionAnswerHandler;
}) {
  const argumentsValue =
    event.arguments && typeof event.arguments === "object"
      ? (event.arguments as Record<string, unknown>)
      : {};
  const approvalId = approval?.id ?? event.approvalId;
  const approvalStatus =
    approval?.status ??
    (event.status === "awaiting-approval" ? "pending" : undefined);
  const command =
    approval?.command ??
    (typeof argumentsValue.command === "string" ? argumentsValue.command : "");
  const cwd =
    approval?.cwd ??
    (typeof argumentsValue.cwd === "string" ? argumentsValue.cwd : null);
  const approvalReason = approval?.approvalReason ?? event.approvalReason;
  const asyncCommand = isAsyncCommand(event.name, event.result);
  const visualStatus = asyncCommand
    ? asyncCommandState(event.result, event.status, event.error)
    : event.status;
  const toolVisual = toolPresentation(event.name, event.displayName);
  const waitingForInteraction =
    approvalStatus === "pending" || event.status === "awaiting-question";
  const { open, onToggle } = useInteractionDisclosure(waitingForInteraction);
  if (event.name === "update_todo_list")
    return <TodoUpdateActivity event={event} />;
  if (event.name === "ask_user_question") {
    if (
      event.status === "awaiting-question" &&
      event.questionId &&
      onQuestionAnswer
    )
      return (
        <QuestionPrompt event={event} busy={busy} onAnswer={onQuestionAnswer} />
      );
    if (event.status === "completed") return <AnsweredQuestion event={event} />;
  }
  const artifactResult =
    (event.name === "artifact_vault" || event.name === "document_studio") &&
    event.status === "completed" &&
    event.result &&
    typeof event.result === "object" &&
    ("artifact" in event.result || "artifacts" in event.result);
  if (artifactResult)
    return (
      <div className="rounded-2xl border border-lumen-200/80 bg-gradient-to-br from-lumen-50 via-white to-emerald-50/60 p-3 shadow-sm dark:border-lumen-900/60 dark:from-lumen-950/30 dark:via-[#151b19] dark:to-emerald-950/20">
        {event.startedAt && (
          <div
            className="mb-2 text-right text-[10px] text-stone-400 tabular-nums"
            title={new Date(event.startedAt).toLocaleString()}
          >
            {new Date(event.startedAt).toLocaleTimeString()}
            {event.finishedAt
              ? ` · ${formatToolDuration(event.startedAt, event.finishedAt)}`
              : ""}
          </div>
        )}
        <ArtifactResult result={event.result} />
        <details className="group mt-3 border-t border-lumen-200/70 pt-2 text-xs dark:border-white/10">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 px-1 font-semibold text-stone-400 marker:content-none hover:text-stone-600 dark:hover:text-stone-200">
            <ChevronDown
              size={13}
              className="transition-transform group-open:rotate-180"
            />
            {t("Tool details")}
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto rounded-xl bg-black/[0.04] p-3 text-[11px] leading-5 text-stone-500 dark:bg-white/[0.04] dark:text-stone-400">
            {JSON.stringify(
              { arguments: event.arguments, result: event.result },
              null,
              2,
            )}
          </pre>
        </details>
      </div>
    );
  return (
    <details
      open={open}
      onToggle={onToggle}
      className={`group min-w-0 overflow-hidden rounded-xl border text-xs ${toolVisual.cardClass}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 p-3 font-semibold text-stone-600 transition-colors marker:content-none hover:bg-black/[0.02] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-sky-500 dark:text-stone-300 dark:hover:bg-white/[0.03]">
        <span
          className={`grid size-8 flex-none place-items-center rounded-lg ${toolVisual.iconClass}`}
        >
          {toolVisual.icon}
        </span>
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="truncate font-semibold text-stone-800 dark:text-stone-100">
            {toolVisual.label}
          </div>
          {event.workspaceName && (
            <div className="truncate text-[10px] font-normal text-sky-700 dark:text-sky-300">
              {t("Workspace: {{name}}", { name: event.workspaceName })}
            </div>
          )}
          {event.repositoryName && (
            <div className="truncate text-[10px] font-normal text-violet-700 dark:text-violet-300">
              {t("Repository: {{name}}", { name: event.repositoryName })}
            </div>
          )}
          <div className="truncate text-[11px] font-normal text-stone-500 dark:text-stone-400">
            {toolPreview(event)}
          </div>
          <code className="text-[10px] font-normal text-stone-400">
            {event.name}
          </code>
          {event.startedAt && (
            <div
              className="text-[10px] font-normal text-stone-400 tabular-nums"
              title={new Date(event.startedAt).toLocaleString()}
            >
              {new Date(event.startedAt).toLocaleTimeString()}
              {event.startedAt && event.finishedAt
                ? ` · ${formatToolDuration(event.startedAt, event.finishedAt)}`
                : ""}
            </div>
          )}
        </div>
        <span
          className={`ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] ${visualStatus === "completed" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : visualStatus === "failed" ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300" : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}
        >
          {visualStatus === "running" && (
            <LoaderCircle
              size={11}
              className="animate-spin motion-reduce:animate-none"
            />
          )}
          {visualStatus === "completed" && <CheckCircle2 size={11} />}
          {visualStatus === "failed" && <ShieldAlert size={11} />}
          {visualStatus === "awaiting-approval"
            ? "Approval needed"
            : visualStatus === "awaiting-question"
              ? "Answer needed"
              : t(visualStatus)}
        </span>
        <ChevronDown
          size={14}
          className="shrink-0 text-stone-400 transition-transform group-open:rotate-180"
        />
      </summary>
      {open && (
        <div className="space-y-3 border-t border-stone-200/70 p-3 dark:border-white/10">
          <ToolResultView
            name={event.name}
            argumentsValue={event.arguments}
            result={event.result}
            error={event.error}
            status={event.status}
          />
          {event.status === "running" &&
            onContinue &&
            !busy &&
            !approvalStatus && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="button-primary px-2.5 py-1.5 text-xs"
                  onClick={onContinue}
                >
                  {t("Continue")}
                </button>
              </div>
            )}
          {event.status === "awaiting-question" &&
            event.questionId &&
            onQuestionAnswer && (
              <QuestionPrompt
                event={event}
                busy={busy}
                onAnswer={onQuestionAnswer}
              />
            )}
          {approvalId && approvalStatus && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
              <div className="flex gap-2">
                <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <strong>
                    {approvalStatus === "pending"
                      ? `Approve command${approval?.workspaceName ? ` in ${approval.workspaceName}` : ""}?`
                      : `${approvalStatus === "approved" || approvalStatus === "executed" ? "Approved" : "Denied"} command is waiting for the other decisions`}
                  </strong>
                  {approvalReason && (
                    <p className="mt-2 rounded border border-amber-300/70 bg-white/50 px-2 py-1.5 text-[11px] leading-5 dark:border-amber-800/70 dark:bg-black/10">
                      <span className="font-semibold">
                        {t("Automatic review note:")}
                      </span>{" "}
                      {approvalReason}
                    </p>
                  )}
                  {command && (
                    <pre className="mt-2 max-h-40 overflow-auto rounded bg-black/5 p-2 font-mono text-xs whitespace-pre-wrap dark:bg-black/20">
                      {command}
                    </pre>
                  )}
                  {cwd && (
                    <p className="mt-1 text-[11px] opacity-70">
                      {t("Working folder:")}
                      <code>{cwd}</code>
                    </p>
                  )}
                  {onApproval ? (
                    <div className="mt-3 flex gap-2">
                      {approvalStatus === "pending" ? (
                        <WorkspaceApprovalActions
                          approvalId={approvalId}
                          command={command}
                          busy={busy}
                          onApproval={onApproval}
                        />
                      ) : (
                        <button
                          type="button"
                          className="button-primary px-2.5 py-1.5 text-xs"
                          disabled={busy}
                          onClick={() =>
                            onContinue
                              ? onContinue()
                              : onApproval(
                                  approvalId,
                                  approvalStatus === "approved"
                                    ? "approved"
                                    : "denied",
                                )
                          }
                        >
                          {busy && (
                            <LoaderCircle className="animate-spin" size={13} />
                          )}
                          {t("Continue")}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="mt-3 block text-[11px] opacity-70">
                      {approvalStatus === "pending"
                        ? t("Waiting for command approval…")
                        : t("Decide the remaining commands to continue.")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </details>
  );
}

function toolPreview(event: ToolEvent) {
  const input = asRecord(event.arguments) ?? {};
  const result = asRecord(event.result) ?? {};
  if (isAsyncCommand(event.name, event.result)) {
    return (
      stringValue(result.command) ||
      stringValue(input.command) ||
      stringValue(result.commandId) ||
      stringValue(input.commandId) ||
      t("Background command")
    );
  }
  if (event.name === "agent_workspace_shell") {
    return stringValue(input.command) || t("Waiting for command");
  }
  if (event.name === "calculate") {
    const expression =
      stringValue(result.expression) || stringValue(input.expression);
    const value = result.result;
    return expression && value !== undefined
      ? `${expression} = ${String(value)}`
      : expression || t("Waiting for calculation");
  }
  if (
    event.name === "ollama_web_search" ||
    event.name === "tavily_web_search"
  ) {
    const count = Array.isArray(result.results) ? result.results.length : 0;
    return `${stringValue(input.query) || t("Web search")} · ${count} ${count === 1 ? t("result") : t("results")}`;
  }
  if (event.name === "ollama_web_fetch" || event.name === "tavily_web_fetch") {
    return (
      stringValue(result.title) ||
      stringValue(input.url) ||
      t("Reading webpage")
    );
  }
  if (event.name === "send_email_notification") {
    return (
      stringValue(result.subject) ||
      stringValue(input.subject) ||
      stringValue(input.to) ||
      t("Sending email")
    );
  }
  if (event.name === "send_whatsapp_message") {
    return (
      stringValue(input.message) ||
      stringValue(input.to) ||
      t("Sending WhatsApp message")
    );
  }
  if (event.name === "artifact_vault" || event.name === "document_studio") {
    return (
      stringValue(result.artifact && asRecord(result.artifact)?.name) ||
      stringValue(input.name) ||
      stringValue(input.operation) ||
      t("Managing artifacts")
    );
  }
  if (event.name === "agent_workspace_upload_artifact")
    return stringValue(input.path) || t("Uploading artifact");
  if (event.name === "workspace_read")
    return stringValue(input.path) || t("Reading workspace file");
  if (
    event.name === "workspace_overwrite" ||
    event.name === "workspace_write" ||
    event.name === "workspace_edit"
  )
    return stringValue(input.path) || t("Writing workspace file");
  if (event.name === "workspace_patch")
    return stringValue(input.path) || t("Updating workspace file");
  if (event.name === "workspace_list")
    return stringValue(input.path) || t("Listing workspace files");
  if (event.name === "workspace_rg")
    return stringValue(input.query) || t("Searching workspace files");
  if (event.name === "workspace_glob")
    return stringValue(input.pattern) || t("Finding workspace paths");
  if (event.name.startsWith("proxmox_"))
    return titleCase(event.name.replace("proxmox_", ""));
  if (stringValue(result.message)) return stringValue(result.message);
  if (stringValue(input.operation))
    return titleCase(stringValue(input.operation));
  if (stringValue(input.action)) return titleCase(stringValue(input.action));
  return event.status === "completed" ? t("Completed") : t("Running");
}

function titleCase(value: string) {
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
